import { datadogRum } from '@datadog/browser-rum'
import { addReactError, reactPlugin } from '@datadog/browser-rum-react'
import type { ErrorInfo } from 'react'

import type {
  BrowserErrorApp,
  JoyfulAppDatadogConfig,
  JoyfulAppRuntime,
  JoyfulAppTelemetryConfig,
  NavbarApp,
} from './appContext'
import {
  ErrorLikeSchema,
  reportBrowserError,
  startBrowserErrorReporter,
} from './browserErrorReporter'

export interface BuildJoyfulAppTelemetryOptions {
  app: BrowserErrorApp
  appSurface?: NavbarApp
  commitSha?: string
  datadogApplicationId?: string
  datadogClientToken?: string
  datadogService?: string
  datadogSite?: string
  isBrowserExtension?: boolean
  usesReactRouter?: boolean
}

export function buildJoyfulAppTelemetry(
  options: BuildJoyfulAppTelemetryOptions,
): JoyfulAppTelemetryConfig {
  return {
    app: options.app,
    datadog: buildDatadogConfig(options),
    release: options.commitSha,
  }
}

function buildDatadogConfig(
  options: BuildJoyfulAppTelemetryOptions,
): JoyfulAppDatadogConfig | undefined {
  const applicationId = options.datadogApplicationId
  const clientToken = options.datadogClientToken
  if (!applicationId || !clientToken) {
    return undefined
  }

  return {
    appSurface: options.appSurface,
    applicationId,
    clientToken,
    isBrowserExtension: options.isBrowserExtension,
    service: options.datadogService,
    site: options.datadogSite,
    usesReactRouter: options.usesReactRouter,
    version: options.commitSha,
  }
}

export function startAnalysisGenerationTelemetry(
  operation: 'analysis_streamable_text' | 'encounter_root_cause_analysis',
) {
  const startedAt = performance.now()
  let hasFinished = false

  return (outcome: 'success' | 'error' | 'aborted') => {
    if (hasFinished) {
      return
    }

    hasFinished = true
    if (outcome === 'error') {
      reportBrowserError({
        errorName: 'AnalysisGenerationError',
        kind: 'analysis_generation_failed',
        message: 'Analysis generation failed',
      })
    }

    if (!hasInitializedDatadogRum) {
      return
    }

    const durationMs = performance.now() - startedAt
    const context = { duration_ms: durationMs, operation, outcome }
    datadogRum.addAction('analysis_generation', context)
    if (outcome === 'error') {
      datadogRum.addError('Analysis generation failed', context)
    }
  }
}

export function trackAnalysisDrillOpen({
  analysisId,
  opensIn,
  segmentKey,
  target,
  widgetIdentifier,
}: {
  analysisId: string | null
  opensIn: 'panel' | 'encounters_list'
  segmentKey: string | null
  target: 'analysis' | 'encounters'
  widgetIdentifier: string | null
}) {
  if (!hasInitializedDatadogRum) {
    return
  }

  datadogRum.addAction('analysis_drill_open', {
    analysis_id: analysisId,
    opens_in: opensIn,
    segment_key: segmentKey,
    target,
    widget_identifier: widgetIdentifier,
  })
}

export function reportCaughtError({
  error,
  errorInfo = {},
}: {
  error: unknown
  errorInfo?: ErrorInfo
}) {
  addReactError(error, errorInfo)
  const parsedError = ErrorLikeSchema.safeParse(error)
  reportBrowserError({
    errorName: parsedError.data?.name,
    kind: 'react_error_boundary',
    message:
      parsedError.data?.message ?? 'Non-error value caught by error boundary',
    stack: parsedError.data?.stack,
  })
}

let hasInitializedDatadogRum = false

export function initializeJoyfulAppTelemetry(app: JoyfulAppRuntime): boolean {
  const telemetry = app.telemetry
  if (telemetry) {
    startBrowserErrorReporter({
      api: app.api,
      app: telemetry.app,
      release: telemetry.release ?? telemetry.datadog?.version,
    })
  }

  if (hasInitializedDatadogRum) {
    return true
  }

  const datadog = telemetry?.datadog
  if (!datadog) {
    return false
  }

  if (datadog.appSurface) {
    datadogRum.setGlobalContextProperty('app_surface', datadog.appSurface)
  }

  datadogRum.init({
    allowedTracingUrls: [app.appUrls.apiBaseUrl],
    allowedTrackingOrigins: datadog.isBrowserExtension
      ? [globalThis.location.origin]
      : undefined,
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    defaultPrivacyLevel: 'mask-user-input',
    env: app.environment,
    plugins: [reactPlugin({ router: datadog.usesReactRouter ?? true })],
    service: datadog.service ?? 'joyful-web-app',
    // Browser extensions display credentials/claim data that must never land
    // in a session replay recording, so replay stays fully disabled there.
    sessionPersistence: datadog.isBrowserExtension
      ? ['cookie', 'local-storage']
      : undefined,
    sessionReplaySampleRate: datadog.isBrowserExtension
      ? 0
      : app.environment === 'production'
        ? 20
        : 100,
    sessionSampleRate: 100,
    site: datadog.site ?? 'datadoghq.com',
    // Trace headers force a CORS preflight, so only a sample of API calls carry them.
    traceSampleRate: 10,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: datadog.version,
  })
  hasInitializedDatadogRum = true

  return true
}
